import React from "react";

export const pageWrapStyle: React.CSSProperties = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top, rgba(59,130,246,0.16), transparent 28%), radial-gradient(circle at right top, rgba(96,165,250,0.08), transparent 26%), #060f1f",
  color: "#e5eefc",
  padding: "16px 12px 48px",
};

export const shellStyle: React.CSSProperties = {
  maxWidth: 1560,
  margin: "0 auto",
  display: "grid",
  gap: 16,
};

export const panelStyle: React.CSSProperties = {
  background: "linear-gradient(180deg, rgba(10,18,35,0.96), rgba(7,13,26,0.96))",
  border: "1px solid rgba(96,165,250,0.16)",
  borderRadius: 20,
  boxShadow:
    "0 0 0 1px rgba(59,130,246,0.05), 0 18px 50px rgba(0,0,0,0.38)",
  backdropFilter: "blur(12px)",
};

export const subtlePanelStyle: React.CSSProperties = {
  background: "rgba(2,6,23,0.58)",
  border: "1px solid rgba(96,165,250,0.14)",
  borderRadius: 16,
};

export const controlLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: 0.3,
  textTransform: "uppercase",
  color: "#93c5fd",
};

export const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(2, 6, 23, 0.96)",
  color: "#eff6ff",
  border: "1px solid rgba(96, 165, 250, 0.22)",
  borderRadius: 12,
  padding: "11px 12px",
  outline: "none",
  fontSize: 14,
};

export const primaryButtonStyle: React.CSSProperties = {
  background:
    "linear-gradient(180deg, rgba(37,99,235,0.98), rgba(29,78,216,0.98))",
  color: "#ffffff",
  border: "1px solid rgba(191,219,254,0.2)",
  borderRadius: 12,
  padding: "11px 14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 8px 20px rgba(37,99,235,0.24)",
};

export const secondaryButtonStyle: React.CSSProperties = {
  background: "rgba(15, 23, 42, 0.96)",
  color: "#dbeafe",
  border: "1px solid rgba(96, 165, 250, 0.22)",
  borderRadius: 12,
  padding: "11px 14px",
  fontWeight: 800,
  cursor: "pointer",
};

export const dangerButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  color: "#fecaca",
  border: "1px solid rgba(239,68,68,0.3)",
};

export function ActionButton({
  children,
  onClick,
  disabled,
  style,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  style: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...style,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

export function SectionTitle({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 900,
          color: "#eff6ff",
          letterSpacing: -0.3,
        }}
      >
        {title}
      </div>
      <div
        style={{
          color: "#93c5fd",
          marginTop: 4,
          lineHeight: 1.45,
          fontSize: 14,
        }}
      >
        {subtitle}
      </div>
    </div>
  );
}

export function MiniStatCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ ...subtlePanelStyle, padding: 12 }}>
      <div style={{ ...controlLabelStyle, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: "#eff6ff" }}>{value}</div>
    </div>
  );
}

export function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ ...subtlePanelStyle, padding: 12 }}>
      <div style={{ ...controlLabelStyle, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 900, color: "#eff6ff" }}>{value}</div>
    </div>
  );
}

export function DataTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ ...subtlePanelStyle, padding: 12 }}>
      <div style={{ ...controlLabelStyle, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: "#eff6ff" }}>{value}</div>
    </div>
  );
}

export function Pill({ text }: { text: string }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "7px 12px",
        background: "rgba(37,99,235,0.1)",
        border: "1px solid rgba(96,165,250,0.18)",
        color: "#dbeafe",
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      {text}
    </div>
  );
}

export function Badge({
  text,
  color,
  bg,
  border,
}: {
  text: string;
  color: string;
  bg: string;
  border: string;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: bg,
        border: `1px solid ${border}`,
        color,
        fontWeight: 800,
        fontSize: 12,
      }}
    >
      {text}
    </div>
  );
}

export function Notice({
  active,
  text,
  tone,
}: {
  active: boolean;
  text: string;
  tone: "red" | "yellow" | "green";
}) {
  const styles =
    tone === "red"
      ? {
          bg: "rgba(239,68,68,0.12)",
          border: "rgba(239,68,68,0.26)",
          color: "#fecaca",
        }
      : tone === "yellow"
      ? {
          bg: "rgba(245,158,11,0.12)",
          border: "rgba(245,158,11,0.26)",
          color: "#fde68a",
        }
      : {
          bg: "rgba(34,197,94,0.12)",
          border: "rgba(34,197,94,0.26)",
          color: "#bbf7d0",
        };

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: styles.bg,
        border: `1px solid ${styles.border}`,
        color: styles.color,
        lineHeight: 1.45,
        fontWeight: 700,
        opacity: active ? 1 : 0.92,
      }}
    >
      {text}
    </div>
  );
}